import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useFormikContext } from "formik";
import { useDispatch } from "react-redux";
import { Icon, useBreakpoints } from "@qgiv/core-react";
import { Ribbon } from "@qgiv/p2p-registration";
import ConnectedDonationInput from "./ConnectedDonationInput";
import "./OptionalDonation.scss";

const OptionalDonation = ({
    displayOptionalDonation = true,
    isNfp = false,
    sectionHeading,
    sectionHeadingForNonFundraisingParticipants,
    description,
    descriptionForNonFundraisingParticipants,
    // Max Donation Amount label is not yet added into Optional Donation
    // maxDonationAmountLabel,
    recipientFinePrintLabel,
    recipientFinePrintLabelForNonFundraisingParticipants,
    updateRegistrationTotals,
    total,
    giftAssist,
    giftAssistAmount,
    optionalDonationAmount,
}) => {
    const dispatch = useDispatch();
    const { values, setFieldValue } = useFormikContext();
    const { Donation_Amount } = values;
    const [donationActive, setDonationActive] = useState(false);

    // -------------------------------------------------------------------------
    // MARK: Update Redux with the new donation amount. However, if for some
    //       reason the user has been able to enter a number less than 0, due to
    //       something such as iMask issues, set the donation amount to 0 as
    //       the donation amount should never be a negative number.
    // -------------------------------------------------------------------------
    useEffect(() => {
        const optionalDonationAmountAsNumber =
            Number(Donation_Amount) > 0 ? Number(Donation_Amount) : 0;

        if (Donation_Amount) {
            setDonationActive(true);
        } else {
            setDonationActive(false);
        }

        if (optionalDonationAmount !== optionalDonationAmountAsNumber) {
            dispatch(
                updateRegistrationTotals({
                    totals: {
                        optionalDonationAmount: optionalDonationAmountAsNumber,
                    },
                }),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Donation_Amount]);

    // -------------------------------------------------------------------------
    // MARK: If there is a $0 registration, and someone adds an optional donation,
    //       then selects to add Gift Assist, and then removes the optional donation,
    //       we want to remove gift assist from being selected
    // -------------------------------------------------------------------------
    useEffect(() => {
        if (
            optionalDonationAmount === 0 &&
            giftAssist === true &&
            giftAssistAmount === total
        ) {
            setFieldValue("Gift_Assist", false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [optionalDonationAmount, total]);

    const { isSmallScreen } = useBreakpoints();

    return (
        displayOptionalDonation && (
            // Keep spacing above and below RegistrationContent consistent
            <div className="col col--12 optional-donation">
                <Ribbon>
                    {isNfp
                        ? sectionHeadingForNonFundraisingParticipants
                        : sectionHeading}
                </Ribbon>
                <div className="-callout -padding-top--10 -padding-bottom--0">
                    <div className="grid">
                        <div className="col col--12 col--sm-9">
                            <div className="grid">
                                <div className="col col--12 -padding-top--20">
                                    <h4>
                                        {isNfp
                                            ? descriptionForNonFundraisingParticipants
                                            : description}
                                    </h4>
                                    <small className="-padding-top--5">
                                        {isNfp
                                            ? recipientFinePrintLabelForNonFundraisingParticipants
                                            : recipientFinePrintLabel}
                                    </small>
                                </div>
                                <div className="col col--12 col--sm-8 -padding-bottom--15">
                                    <div className="optional-donation__input-container">
                                        <ConnectedDonationInput />
                                    </div>
                                    {/* When Max Donation Amount is added into Optional Donation, uncomment below code */}
                                    {/* {maxDonationAmountLabel && (
                                            <p className="-type--small -padding-top--5">
                                                {maxDonationAmountLabel}
                                            </p>
                                        )} */}
                                </div>
                            </div>
                        </div>
                        {!isSmallScreen && (
                            <div className="col col--3  optional-donation__icon">
                                {donationActive ? (
                                    <Icon
                                        className="optional-donation__icon--sm -color--theme-primary"
                                        glyph="qgiv-donation"
                                        type="Custom"
                                    />
                                ) : (
                                    <Icon
                                        className="optional-donation__icon--sm -color--system-light"
                                        glyph="qgiv-donation"
                                        type="Custom"
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    );
};

OptionalDonation.propTypes = {
    displayOptionalDonation: PropTypes.bool.isRequired,
    sectionHeading: PropTypes.string.isRequired,
    sectionHeadingForNonFundraisingParticipants: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    descriptionForNonFundraisingParticipants: PropTypes.string.isRequired,
    // Max Donation Amount label is not yet added into Optional Donation
    // maxDonationAmountLabel: PropTypes.string,
    recipientFinePrintLabel: PropTypes.string.isRequired,
    recipientFinePrintLabelForNonFundraisingParticipants:
        PropTypes.string.isRequired,
    isNfp: PropTypes.bool.isRequired,
    total: PropTypes.number.isRequired,
    giftAssist: PropTypes.bool.isRequired,
    giftAssistAmount: PropTypes.number.isRequired,
    optionalDonationAmount: PropTypes.number.isRequired,
    updateRegistrationTotals: PropTypes.func.isRequired,
};

export default OptionalDonation;
